import { getComments } from "./getComments";
import { getPcms } from "./getPcms";
import { getPostmortemState } from "./getPostmortemState";
import { getQuestionaire } from "./getQuestionaire";
import { removeUnwantedKeys } from "./removeUnwantedKeys";

export const getPostmortemData = (
  currentPostmortemObject,
  postmortemState,
  mode,
  memoizedDispatch,
  step
) => {
  const keysToRemove = [
    "cmdb_ci",
    "createdOn",
    "incidentID",
    "problemID",
    "priority",
    "state",
  ];

  let thisIsMimIncident = false;

  let postmortemObject = JSON.parse(JSON.stringify(currentPostmortemObject));

  if (
    postmortemObject["priority"] === "3" ||
    postmortemObject["priority"] === "4"
  ) {
    thisIsMimIncident = true;
  }

  removeUnwantedKeys(postmortemObject, keysToRemove);

  postmortemObject["portfolio"] = postmortemState.portfolio.label;
  // selectedOnbaordingOptions.selectedPortfolio.label;

  postmortemObject["platform"] = postmortemState.platform.label;
  // selectedOnbaordingOptions.selectedPlatform.label;

  postmortemObject["product"] = postmortemState.product.label;
  // selectedOnbaordingOptions.selectedProduct.label;

  postmortemObject["application"] = postmortemState.app.label;

  postmortemObject["application_id"] =
    postmortemState.application_id.value || postmortemState.app.id;
  // selectedOnbaordingOptions.selectedApp.label;

  // Add MVC Vulnerability Related field (array of values)
  if (postmortemState.mvcVulnerabilityRelated && postmortemState.mvcVulnerabilityRelated.length > 0) {
    postmortemObject["mvcVulnerabilityRelated"] = postmortemState.mvcVulnerabilityRelated.map(item => item.value || item);
  } else {
    postmortemObject["mvcVulnerabilityRelated"] = [];
  }

  if (mode === "submitting") {
    postmortemObject["status"] = getPostmortemState(
      memoizedDispatch,
      postmortemState.postmortemCurrentStatus.currentState,
      postmortemState,
      thisIsMimIncident
    );
    postmortemObject["submitting"] = true;
    postmortemObject["sendNotification"] = step && step === 2 ? true : false;
  } else {
    postmortemObject["submitting"] = false;
    postmortemObject["sendNotification"] = step && step === 2 ? true : false;
    if (postmortemState.postmortemCurrentStatus.currentState === "DRAFT") {
      postmortemObject["status"] = 0;
      // Stay in DRAFT - no automatic status change
    } else {
      if (postmortemState.postmortemCurrentStatus.currentState === "LEADERSHIP_REVIEW") {
        postmortemObject["status"] = 3;
      } else if (
        postmortemState.postmortemCurrentStatus.currentState === "DONE"
      ) {
        postmortemObject["status"] = 7;
      }
    }
  }

  return getPostmortemDataByStep(postmortemObject, postmortemState, step);
};

export const getPostmortemDataByStep = (
  postmortemObject,
  postmortemState,
  step,
  changedFields = {} // Default to empty object if not provided
) => {
  if (step === 0) {


    // Get changed fields from incidentSummaryState if available
    const incidentSummaryChangedFields = postmortemState.changedFileds || {};

    // Check if incident_summary description has changed
    if (incidentSummaryChangedFields.incSummDescription) {
      postmortemObject["incident_summary"] = {
        incidentSummary: postmortemState.incidentSummaryState.incSummDescription,
        comments: getComments(postmortemState.incidentSummaryState.comments),
      };
    } else {
      delete postmortemObject["incident_summary"];
    }

    // Check if preceding events description has changed
    if (incidentSummaryChangedFields.preceEventsDescription) {
      postmortemObject["precedingEvents"] = {
        changeRelated: {
          changeId: "",
          rollbackDone: "",
          changeAssessment: "",
        },
        description:
          postmortemState.incidentSummaryState.preceEventsDescription,
      };
    } else {
      delete postmortemObject["precedingEvents"];
    }

    // Check if impact has changed
    if (incidentSummaryChangedFields.impact) {
      postmortemObject["businessImpact"] = {
        description: postmortemState.incidentSummaryState.impact,
      };
    } else {
      delete postmortemObject["businessImpact"];
    }

    // Check if detection and response description or metrics have changed
    if (
      incidentSummaryChangedFields.detectionAndResponseDescription ||
      incidentSummaryChangedFields.mttd ||
      incidentSummaryChangedFields.mttrespond ||
      incidentSummaryChangedFields.mttrepair ||
      incidentSummaryChangedFields.mttrecover
    ) {
      postmortemObject["detectionResponse"] = {};

      // Only include changed fields in detectionResponse
      if (incidentSummaryChangedFields.detectionAndResponseDescription) {
        postmortemObject["detectionResponse"].description =
          postmortemState.incidentSummaryState.detectionAndResponseDescription;
      }

      if (incidentSummaryChangedFields.mttd) {
        postmortemObject["detectionResponse"].mttd =
          postmortemState.incidentSummaryState.mttd;
      }

      if (incidentSummaryChangedFields.mttrespond) {
        postmortemObject["detectionResponse"].mttrespond =
          postmortemState.incidentSummaryState.mttrespond;
      }

      if (incidentSummaryChangedFields.mttrepair) {
        postmortemObject["detectionResponse"].mttrepair =
          postmortemState.incidentSummaryState.mttrepair;
      }

      if (incidentSummaryChangedFields.mttrecover) {
        postmortemObject["detectionResponse"].mttrecover =
          postmortemState.incidentSummaryState.mttrecover;
      }

      // Always include isCustomMttrepair if any detection/response field changed
      postmortemObject["detectionResponse"].isCustomMttrepair =
        postmortemState.incidentSummaryState.isCustomMttrepair;
    } else {
      delete postmortemObject["detectionResponse"];
    }

    // Delete other sections that aren't part of this step
    delete postmortemObject["recovery"];
    delete postmortemObject["rootCauseIdentificationRequest"];
    delete postmortemObject["rootCause"];
    delete postmortemObject["recurrence"];
    delete postmortemObject["preventiveCorrectiveMeasures"];
  } else if (step === 1) {
    delete postmortemObject["incident_summary"];
    delete postmortemObject["precedingEvents"];
    delete postmortemObject["businessImpact"];
    delete postmortemObject["detectionResponse"];
    delete postmortemObject["preventiveCorrectiveMeasures"];

    // Get changed fields from the relevant state objects
    const recoveryChangedFields = postmortemState.changedFileds || {};
    const rootCauseIdentificationChangedFields =
      postmortemState.changedFileds || {};
    const rootCauseStateChangedFields = postmortemState.changedFileds || {};
    const recurrenceChangedFields = postmortemState.changedFileds || {};




    // Check if recovery has changed
    if (recoveryChangedFields.recovery) {
      postmortemObject["recovery"] = {
        recovery: postmortemState.RecoveryState.recovery,
      };
    } else {
      delete postmortemObject["recovery"];
    }

    // Check if root cause identification has changed
    if (
      rootCauseIdentificationChangedFields.rcidescription ||
      rootCauseIdentificationChangedFields.rciquestions
    ) {
      // Always send both description AND questions together — sending only one
      // causes the API to overwrite the whole record, losing the other field.
      postmortemObject["rootCauseIdentificationRequest"] = {
        description: postmortemState.RootCauseIdentificationState.description,
        questionarie: getQuestionaire(postmortemState.RootCauseIdentificationState),
      };
    } else {
      delete postmortemObject["rootCauseIdentificationRequest"];
    }

    // Check if any root cause fields have changed
    if (
      rootCauseStateChangedFields.cause ||
      rootCauseStateChangedFields.rootCause ||
      rootCauseStateChangedFields.rootCauseCode ||
      rootCauseStateChangedFields.rootCauseSubCode ||
      rootCauseStateChangedFields.changeRelated ||
      rootCauseStateChangedFields.causedByChangeNumber ||
      rootCauseStateChangedFields.causedByVendor ||
      rootCauseStateChangedFields.tags ||
      rootCauseStateChangedFields.subCategory ||
      // rootCauseStateChangedFields.changeIsRequired
      rootCauseStateChangedFields.impactedByChange

    ) {
      postmortemObject["rootCause"] = {};

      // Only include fields that have actually changed
      if (rootCauseStateChangedFields.cause) {
        postmortemObject["rootCause"].cause =
          postmortemState.RootCauseState.cause || "";
      }

      if (rootCauseStateChangedFields.rootCauseCode) {
        postmortemObject["rootCause"].rootCauseCode =
          postmortemState.RootCauseState.rootCauseCode || "";
      }

      if (rootCauseStateChangedFields.rootCauseSubCode) {
        postmortemObject["rootCause"].rootCauseSubCode =
          postmortemState.RootCauseState.rootCauseSubCode || "";
      }

      if (rootCauseStateChangedFields.changeRelated) {
        postmortemObject["rootCause"].changeRelated =
          postmortemState.RootCauseState.changeRelated || "";
      }

      if (rootCauseStateChangedFields.causedByChangeNumber) {
        postmortemObject["rootCause"].causedByChangeNumber = postmortemState.RootCauseState.causedByChangeNumber || "";
      }



      if (rootCauseStateChangedFields.causedByVendor) {
        postmortemObject["rootCause"].causedByVendor =
          postmortemState.RootCauseState.causedByVendor || "";
      }

      if (rootCauseStateChangedFields.tags) {
        postmortemObject["rootCause"].tags = [
          ...postmortemState.RootCauseState.tags,
        ];
      }

      // Include sub-category (type-2 root cause) when changed
      if (rootCauseStateChangedFields.subCategory) {
        // Support both `subCategory` and `rootCauseSubCategory` keys in state
        postmortemObject["rootCause"].rootCauseSubCategory =
          postmortemState.RootCauseState.subCategory ||
          postmortemState.RootCauseState.rootCauseSubCategory ||
          "";
      }

      // if( rootCauseStateChangedFields.changeIsRequired) {
      //   postmortemObject["rootCause"]["changeIsRequired"] =
      //     postmortemState.RootCauseState.changeIsRequired;
      // }
      if( rootCauseStateChangedFields.impactedByChange) {
        postmortemObject["rootCause"]["impactedByChange"] =
          postmortemState.RootCauseState.impactedByChange;
      }



      // Always include comments if any root cause field changed
      postmortemObject["rootCause"].comments = [...getComments(postmortemState.RootCauseState.comments)];
      
      // Only include description if it exists (to avoid sending undefined)
      if (postmortemState.RootCauseState.description !== undefined) {
        postmortemObject["rootCause"].description = postmortemState.RootCauseState.description;
      }
      
      if (rootCauseStateChangedFields.vendorDetails) {
        postmortemObject["rootCause"].vendorDetails = postmortemState.RootCauseState.vendorDetails;
      }

    } else {
      delete postmortemObject["rootCause"];
    }

    // Check if recurrence has changed
    if (
      recurrenceChangedFields.recurrence ||
      recurrenceChangedFields.count ||
      recurrenceChangedFields.incidentId ||
      recurrenceChangedFields.problemId ||
      recurrenceChangedFields.description ||
      recurrenceChangedFields.recurringIssue
    ) {
      postmortemObject["recurrence"] = {};

      if(recurrenceChangedFields.recurringIssue) {
        postmortemObject["recurrence"]["recurringIssue"] =
          postmortemState.RecurrenceState.recurringIssue;
      }

      // Only include changed fields in recurrence
      if (recurrenceChangedFields.recurrence) {
        postmortemObject["recurrence"].recurrence =
          postmortemState.RecurrenceState.recurrence;
      }

      if (recurrenceChangedFields.count) {
        postmortemObject["recurrence"].count =
          postmortemState.RecurrenceState.count;
      }

      // Handle pastReferences as a group if any of its fields changed
      if (
        recurrenceChangedFields.incidentId ||
        recurrenceChangedFields.problemId ||
        recurrenceChangedFields.description
      ) {
        postmortemObject["recurrence"].pastReferences = {};

        if (recurrenceChangedFields.incidentId) {
          postmortemObject["recurrence"].pastReferences.incidentId =
            postmortemState.RecurrenceState.incidentId;

            postmortemObject["recurrence"].pastReferences.problemId =
            postmortemState.RecurrenceState.problemId;
        }

        if (recurrenceChangedFields.problemId) {
          postmortemObject["recurrence"].pastReferences.problemId =
            postmortemState.RecurrenceState.problemId;

            postmortemObject["recurrence"].pastReferences.incidentId =
            postmortemState.RecurrenceState.incidentId;

        }

        if (recurrenceChangedFields.description) {
          postmortemObject["recurrence"].pastReferences.description =
            postmortemState.RecurrenceState.description;
        }
      }

      // The list is authoritative on the server, so removals are persisted too.
      if (
        Array.isArray(postmortemState.RecurrenceState.pastReferencesList) &&
        (recurrenceChangedFields.pastReferencesList ||
          recurrenceChangedFields.incidentId ||
          recurrenceChangedFields.problemId)
      ) {
        postmortemObject["recurrence"].pastReferencesList =
          postmortemState.RecurrenceState.pastReferencesList;
      }
    } else {
      delete postmortemObject["recurrence"];
    }
  } else if (step === 2 || step === 3) {
    delete postmortemObject["recovery"];
    delete postmortemObject["rootCauseIdentificationRequest"];
    delete postmortemObject["rootCause"];
    delete postmortemObject["recurrence"];
    delete postmortemObject["incident_summary"];
    delete postmortemObject["precedingEvents"];
    delete postmortemObject["businessImpact"];
    delete postmortemObject["detectionResponse"];

    // Get changed fields from preventive and corrective measures state
    const pcmChangedFields = postmortemState.changedFileds || {};


    // Check if preventive and corrective measures have changed
    if (pcmChangedFields.pTasks || pcmChangedFields.jiraTickets) {
      postmortemObject["preventiveCorrectiveMeasures"] = [{}];

      // Only include changed fields in preventive corrective measures
      if (pcmChangedFields.pTasks || pcmChangedFields.jiraTickets) {
        const fullPcm = getPcms(
          postmortemState.PreventiveAndCorrectiveMeasuresState
        );

        if (pcmChangedFields.pTasks) {
  
          postmortemObject["preventiveCorrectiveMeasures"][0]["ptask"] =
            fullPcm[0].ptask;
        }

        if (pcmChangedFields.jiraTickets) {
          postmortemObject["preventiveCorrectiveMeasures"][0]["jira"] =
            fullPcm[0].jira;
        }

        // Always include description and comments if any PCM field changed

        postmortemObject["preventiveCorrectiveMeasures"][0]["comments"] =
          fullPcm[0].comments;
      }
    } else {
      delete postmortemObject["preventiveCorrectiveMeasures"];
    }
  }

  postmortemObject["postmortemIncomplete"] =
    postmortemState?.postmortemIsIncompleteState?.incomplete;

  return postmortemObject;
};
